import { isTransporterDepartureDay } from '@carsfinders/contracts';

import { t } from '../../../../lib/i18n';
import { cn } from '../../utils';
import type { ScheduleCalendarCell, ScheduleSessionView } from './session-schedule-types';
import { dayDiff, viewsOnDay } from './session-schedule-utils';

/**
 * Design D's month calendar, read-only — admin port of
 * `apps/auction/.../components/AuctionScheduleCalendarReadOnly.tsx`.
 *
 * The admin SPA has one artboard width (desktop-first, `lg:` breakpoint), so
 * this keeps only the web original's desktop grid; the web file's separate
 * 390px collapsed-dot markup is not ported — there is no matching admin
 * viewport to render it on.
 */

interface SessionCalendarViewProps {
  cells: ScheduleCalendarCell[];
  views: ScheduleSessionView[];
  /** Today in the display zone, `YYYY-MM-DD`. */
  today: string;
  selected: string;
  monthLabel: string;
  /** Monday-first weekday headings. */
  weekdays: string[];
  onSelect: (_date: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
}

const MAX_CHIPS = 2;

export function SessionCalendarView({
  cells,
  views,
  today,
  selected,
  monthLabel,
  weekdays,
  onSelect,
  onPrevMonth,
  onNextMonth,
  onToday,
}: SessionCalendarViewProps) {
  // A transporter marker is only meaningful ahead of today.
  const showsTransporter = (cell: ScheduleCalendarCell) =>
    !cell.outside && isTransporterDepartureDay(cell.date) && dayDiff(today, cell.date) >= 0;

  return (
    <section className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface">
      <div className="flex items-center gap-1.5 border-b border-cf-line-soft px-[18px] py-3.5">
        <button
          type="button"
          aria-label={t('auctionSchedule.calendar.previousMonth')}
          onClick={onPrevMonth}
          className="h-9 w-9 cursor-pointer rounded-cf-md border border-cf-line bg-cf-surface text-[16px] text-cf-ink-muted"
        >
          ‹
        </button>
        <button
          type="button"
          aria-label={t('auctionSchedule.calendar.nextMonth')}
          onClick={onNextMonth}
          className="h-9 w-9 cursor-pointer rounded-cf-md border border-cf-line bg-cf-surface text-[16px] text-cf-ink-muted"
        >
          ›
        </button>
        <h2 className="mr-auto ml-2.5 text-[20px] font-semibold tracking-[-0.01em]">{monthLabel}</h2>
        <button
          type="button"
          onClick={onToday}
          className="h-8 cursor-pointer rounded-full border border-cf-line bg-cf-surface px-3 text-[13px] font-semibold"
        >
          {t('auctionSchedule.calendar.today')}
        </button>
      </div>

      <div className="grid grid-cols-7 border-b border-cf-line-soft">
        {weekdays.map((weekday) => (
          <div
            key={weekday}
            className="px-2.5 py-2 text-left font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase"
          >
            {weekday}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-cf-line-soft">
        {cells.map((cell) => {
          const dayViews = viewsOnDay(views, cell.date);
          const shown = dayViews.slice(0, MAX_CHIPS);
          const hidden = dayViews.length - shown.length;
          const isToday = cell.date === today;
          const isSelected = cell.date === selected;

          return (
            <button
              key={cell.date}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(cell.date)}
              className={cn(
                'flex min-h-24 min-w-0 cursor-pointer flex-col items-stretch gap-1 overflow-hidden border-0 p-2 text-left',
                isSelected
                  ? 'bg-cf-brand/4 outline outline-2 -outline-offset-2 outline-cf-brand'
                  : 'bg-cf-surface',
                cell.outside && 'opacity-45',
              )}
            >
              <span className="flex items-center justify-between">
                <span
                  className={cn(
                    'inline-flex h-6 w-6 items-center justify-center rounded-cf-sm font-mono text-[12px]',
                    isToday && 'bg-cf-accent text-cf-ink',
                    !isToday && cell.outside && 'text-cf-ink-soft',
                    !isToday && !cell.outside && 'text-cf-ink-muted',
                  )}
                >
                  {cell.day}
                </span>
                {showsTransporter(cell) && (
                  <span
                    title={t('auctionSchedule.calendar.transporterTitle')}
                    className="font-mono text-[9px] tracking-[0.08em] text-cf-ink-subtle"
                  >
                    {t('auctionSchedule.calendar.transporter')}
                  </span>
                )}
              </span>

              {shown.map((view) => (
                <span
                  key={view.session.id}
                  className={cn(
                    'flex justify-between gap-1 overflow-hidden rounded-[5px] border-l-[3px] px-1.5 py-[3px] text-[11px] leading-[1.3] whitespace-nowrap',
                    view.chip,
                  )}
                  style={{ borderLeftColor: view.dot }}
                >
                  <span className="overflow-hidden text-ellipsis">{view.session.house.shortName}</span>
                  <span className="font-mono">{view.session.time}</span>
                </span>
              ))}

              {hidden > 0 && (
                <span className="pl-1.5 text-[11px] text-cf-ink-subtle">
                  {t('auctionSchedule.calendar.more', { count: hidden })}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-[18px] border-t border-cf-line-soft px-[18px] py-3 text-[12px] text-cf-ink-muted">
        <LegendSwatch color="var(--color-cf-accent)" label={t('auctionSchedule.legend.soon')} />
        <LegendSwatch color="var(--color-cf-past)" label={t('auctionSchedule.legend.past')} />
        <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.08em] text-cf-ink-subtle">
          {t('auctionSchedule.legend.transporter')}
        </span>
      </div>
    </section>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px]" style={{ background: color }} />
      {label}
    </span>
  );
}
